import { useEffect, useMemo, useState } from 'react';

import { useAuthContext } from '@/features/auth/AuthContext';
import type { DictKey } from '@/i18n/en';
import { useT } from '@/i18n/LocaleProvider';

import { type AuditEntry, createModerationApiClient } from './api';
import { hasModeratorScope } from './permissions';

export type AuditLogProps = {
  onLogin?: () => void;
  onRegister?: () => void;
};

export function AuditLog({ onLogin, onRegister }: AuditLogProps) {
  const t = useT();
  const auth = useAuthContext();
  const canModerate = hasModeratorScope(auth.account);
  const api = useMemo(() => createModerationApiClient(() => auth.token), [auth.token]);

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canModerate) {
      setEntries([]);
      setNextBefore(null);
      setError(null);
      return;
    }

    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const result = await api.listAuditLog({ limit: 25 });

        if (!cancelled) {
          setEntries(result.entries);
          setNextBefore(result.next_before ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err, t('audit_log_error_load')));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [api, canModerate, t]);

  async function loadMore() {
    if (!nextBefore || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);
    setError(null);

    try {
      const result = await api.listAuditLog({ limit: 25, before: nextBefore });

      setEntries((prev) => [...prev, ...result.entries]);
      setNextBefore(result.next_before ?? null);
    } catch (err) {
      setError(errorMessage(err, t('audit_log_error_load')));
    } finally {
      setIsLoadingMore(false);
    }
  }

  if (!auth.isAuthenticated) {
    return (
      <article className="note" data-testid="audit-log">
        <header className="note-header">
          <h1>{t('audit_log_title')}</h1>
        </header>
        <div className="auth-required">
          <strong>{t('moderation_moderator_required')}</strong>
          <div className="form-actions">
            <button className="primary-action" type="button" onClick={onLogin}>
              {t('auth_login')}
            </button>
            <button className="text-action" type="button" onClick={onRegister}>
              {t('auth_register')}
            </button>
          </div>
        </div>
      </article>
    );
  }

  if (!canModerate) {
    return (
      <article className="note" data-testid="audit-log">
        <header className="note-header">
          <h1>{t('audit_log_title')}</h1>
        </header>
        <div className="auth-required" data-testid="audit-log-forbidden">
          <strong>{t('moderation_forbidden')}</strong>
          <span>{t('moderation_forbidden_hint')}</span>
        </div>
      </article>
    );
  }

  return (
    <article className="note" data-testid="audit-log">
      <header className="note-header">
        <div className="note-kicker">
          <span>{t('moderation_title')}</span>
        </div>
        <h1>{t('audit_log_title')}</h1>
      </header>

      {isLoading ? <div className="empty-note">{t('audit_log_loading')}</div> : null}

      {error ? <div className="form-error">{error}</div> : null}

      {!isLoading && entries.length === 0 && !error ? (
        <div className="empty-note" data-testid="audit-log-empty">
          {t('audit_log_empty')}
        </div>
      ) : null}

      {entries.length > 0 ? (
        <ul className="audit-log-list" data-testid="audit-log-list">
          {entries.map((entry) => (
            <li key={entry.id} className="audit-log-entry">
              <span className="audit-log-time">{formatDate(entry.at)}</span>
              <span className="audit-log-actor">{entry.actor_name}</span>
              <span className="audit-log-action">{formatAction(entry.action, t)}</span>
              <span className="audit-log-target">{entry.target_id}</span>
              {entry.summary ? <span className="audit-log-summary">{entry.summary}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {nextBefore ? (
        <button
          className="text-action"
          type="button"
          disabled={isLoadingMore}
          onClick={() => void loadMore()}
        >
          {isLoadingMore ? t('moderation_load_more_loading') : t('moderation_load_more')}
        </button>
      ) : null}
    </article>
  );
}

type TFn = (key: DictKey, vars?: Record<string, string>) => string;

function formatAction(action: string, t: TFn): string {
  const key = `audit_action_${action.replace('.', '_')}` as DictKey;
  const label = t(key);
  return label !== key ? label : action;
}

function formatDate(value: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }

  return fallback;
}
